import React, { useEffect, useState } from 'react'
import { WithContext as ReactTags } from 'react-tag-input'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import { useField, ErrorMessage } from 'formik'

import RemoveIcon from '@assets/images/icons/close.svg'

import PropTypes from 'prop-types'
import SvgIcon from '../../svg-icon/svg-icon'

const EMPTY_TAG = 'Keine Angabe'

const KeyCodes = {
  comma: 188,
  enter: 13,
}
const delimiters = [KeyCodes.comma, KeyCodes.enter]

const useStyles = makeStyles(theme => ({
  tags: {
    position: 'relative',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    padding: '10px 5px',
  },
  focused: {},
  tag: {},
  remove: {},
  tagInputField: {},
  tagInput: {
    width: 'auto',
    display: 'inline-block',
    '& $tagInputField': {
      height: 32,
      margin: 0,
      fontSize: 14,
      outline: 'none',
      border: '1px dashed var(--color-border)',
      borderRadius: 16,
      background: 'transparent',
      padding: '2px 17px 2px 20px',
      textAlign: 'center',
      '&:focus': {
        '::placeholder': {
          color: 'var(--color-border)',
        },
      },
      '&::placeholder': {
        color: 'var(--color-primary-light)',
      },
    },
  },
  selected: {
    '& $tag': {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: '1px solid var(--color-border)',
      borderRadius: 16,
      background: theme.backgroundColor,
      fontSize: 14,
      padding: '2px 17px 2px 20px',
      margin: '0 10px 10px 0',
      cursor: 'move',
      '& $remove': {
        cursor: 'pointer',
        color: 'var(--color-primary-light)',
        marginLeft: 14,
        marginTop: -1,
        fontSize: 20,
      },
    },
  },
  label: {
    display: 'flex',
    alignSelf: 'flex-start',
    color: '#414a5f',
    fontSize: 13,
    marginBottom: 10,
  },
  error: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: 12,
    color: theme.red,
    marginTop: 10,
  },
  removeWrapper: {
    cursor: 'pointer',
    marginLeft: 10,
    '&:hover svg': {
      fill: theme.error,
    },
  },
}))

const RemoveComponent = ({ onRemove }) => {
  const theme = useTheme()
  const classes = useStyles()

  // Need to add the ReactTags__remove to prevent error in package
  // FYI: https://github.com/react-tags/react-tags/issues/977
  return (
    <div className={clsx('ReactTags__remove', classes.removeWrapper)} onClick={onRemove}>
      <SvgIcon svg={RemoveIcon} height={8} width={8} fill={theme.iconFillColor} />
    </div>
  )
}

const TagInput = props => {
  const {
    tags: initialTags = [],
    name,
    onBlur,
    onChange,
    checkTagBeforeDelete,
    allowDragDrop = true,
    placeholder,
    classNames = {},
    autoFocus = false,
    readOnly,
    label,
    className,
    useFormik = false,
    errorMessage,
    dataQsId,
  } = props

  const classes = useStyles()
  const [tags, setTags] = useState(initialTags)
  const [focused, setFocused] = useState(false)
  const [error, setError] = useState(errorMessage)

  let field = null
  let meta = null
  let helpers = null
  let errorEl = null

  if (useFormik) {
    [field, meta, helpers] = useField(props)
    useEffect(() => { setTags(field.value.map(tag => ({ text: tag, id: tag }))) }, [field.value])
    errorEl = <ErrorMessage name={name} className={classes.error} component="div" />
  } else {
    useEffect(() => { setTags(initialTags.map(tag => ({ text: tag, id: tag }))) }, [initialTags])
    errorEl = error ? <div className={classes.error}>{error}</div> : null
  }

  useEffect(() => { setError(errorMessage) }, [initialTags, errorMessage])

  useEffect(() => {
    return () => {
      setTags([])
    }
  }, [])

  const onContainerClick = () => {
    const input = document.getElementById(name)
    input?.focus()
  }

  const handleFocus = () => {
    setFocused(true)
  }

  const handleBlur = tag => {
    const updatedTags = tags
    if (tag) {
      updatedTags.push({ text: tag, id: tag })
    }

    setTags(updatedTags)
    setFocused(false)

    const values = updatedTags.map(tag => tag.text)
    if (useFormik && helpers) helpers.setValue(values)

    onBlur?.(values, name)
  }

  const onAdd = tag => {
    const updatedTags = [...tags, tag]
    setTags(updatedTags)

    const values = updatedTags.map(tag => tag.text)
    if (useFormik && helpers) helpers.setValue(values)

    onChange?.(values, name)
  }

  const onRemove = tagIndex => {
    const [{ text: tag }] = tags.filter((tag, index) => index === tagIndex)

    if (tag === EMPTY_TAG) {
      return
    }

    const removeTag = () => {
      const updatedTags = tags.filter((tag, index) => index !== tagIndex)

      setTags(updatedTags)

      const values = updatedTags.map(tag => tag.text)
      if (useFormik && helpers) helpers.setValue(values)

      onChange?.(values, name)
    }

    // if checkTagBeforeDelete is specified call it
    if (checkTagBeforeDelete) {
      const mappedTags = tags.map(tag => tag.text)
      return checkTagBeforeDelete(mappedTags, tag, canBeDeleted => {
        if (!canBeDeleted) { return }

        removeTag()
      })
    }

    removeTag()
  }

  const onDrag = (tag, currPos, newPos) => {
    if (!allowDragDrop) return

    const currentTags = [...tags]
    const updatedTags = currentTags.slice()

    updatedTags.splice(currPos, 1)
    updatedTags.splice(newPos, 0, tag)

    // re-render
    setTags(updatedTags)

    const values = updatedTags.map(tag => tag.text)
    if (useFormik && helpers) helpers.setValue(values)

    onChange?.(values, name)
  }

  const mappedClassNames = {
    tags: clsx(classes.tags, classNames.tags),
    tag: clsx(classes.tag, classNames.tag),
    tagInput: clsx(classes.tagInput, classNames.tagInput),
    tagInputField: clsx(classes.tagInputField, classNames.tagInputField),
    selected: clsx(classes.selected, classNames.selected),
    remove: clsx(classes.remove, classNames.remove),
    focused: clsx(classes.focused, classNames.focused),
    container: clsx(classNames.container),
  }

  const labelEl = label ? <label className={classes.label}>{label}</label> : null

  return (
    <div
      className={clsx(focused ? mappedClassNames.focused : '', className, mappedClassNames.container)}
      data-qs-id={dataQsId}
      onClick={onContainerClick}>
      {labelEl}
      <ReactTags
        id={name}
        autofocus={autoFocus}
        tags={tags}
        readOnly={readOnly}
        allowDragDrop={allowDragDrop}
        placeholder={placeholder || ''}
        delimiters={delimiters}
        {...field}
        removeComponent={RemoveComponent}
        handleInputFocus={handleFocus}
        handleInputBlur={handleBlur}
        handleAddition={onAdd}
        handleDelete={onRemove}
        handleDrag={onDrag}
        classNames={mappedClassNames}
      />
      {errorEl}
    </div>
  )
}

TagInput.propTypes = {
  tags: PropTypes.array,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  allowDragDrop: PropTypes.bool,
  classNames: PropTypes.object,
  autoFocus: PropTypes.bool,
  useFormik: PropTypes.bool,
  dataQsId: PropTypes.string.isRequired,
}

export default TagInput
